import React, { useState, useEffect } from "react";
import { ListItemButton, ListItemIcon, ListItemText, Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { FONT_SIZE } from "../../../../constants/fontSizes";
import UnreadBadge from "./UnreadBadge";

/**
 * Sidebar の1項目を描画するコンポーネント
 *  - 初回セットアップ（スキルシートテンプレートがない）の場合：
 *    - 「基本情報」メニューは new に遷移
 *    - 「基本情報」「お知らせ」「退会」「ログアウト」以外のメニューはグレーアウト＆操作不可
 */

// ===== 共通スタイル定義 =====
const TEXT_COLOR = "rgba(255,255,255,0.9)";
const ICON_COLOR = "rgba(255,255,255,0.7)";
const HOVER_BG = "rgba(255,255,255,0.08)";
const ACTIVE_BG = "#4951D0";
const ACTIVE_HOVER_OVERLAY = "rgba(255,255,255,0.12)";
const DIVIDER_COLOR = "rgba(255,255,255,0.12)";

const SidebarItem = ({
  menu,
  onClose,
  onLogout,
  requestNavigate,
  userJobRequestExists,
  hasUnreadNotice = false,
  isSkillSheetBasicCompleted,
}) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  /**
   * 初回セットアップ判定（template未作成）
   */
  const isFirstSetup = isSkillSheetBasicCompleted === false;

  // メニュー種別判定
  const isBasicInfo = menu.label === "基本情報";
  const isNoticeLabel = menu.label === "お知らせ";
  const isWithdrawLabel = menu.label === "退会";
  const isLogoutLabel = menu.label === "ログアウト";

  /**
   * 初回中に操作できるメニュー
   */
  const isAllowedMenu =
    isBasicInfo || isNoticeLabel || isWithdrawLabel || isLogoutLabel;

  /**
   * 初回セットアップ中は一部メニューのみ有効
   */
  const isDisabled = isFirstSetup && !isAllowedMenu;

  /**
   * action メニュー（ログアウト）
   */
  if (menu.type === "action" && menu.action === "logout") {
    return (
      <ListItemButton
        onClick={() => {
          // 未保存があれば確認モーダル → OK後にログアウト実行
          requestNavigate?.(null, () => {
            onClose?.();
            onLogout?.();
          });
        }}
        sx={{
          position: "relative",
          color: TEXT_COLOR,
          borderBottom: `1px solid ${DIVIDER_COLOR}`,
          backgroundColor: "transparent",

          "&:hover": {
            backgroundColor: HOVER_BG,
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: TEXT_COLOR }}>
          <menu.icon />
        </ListItemIcon>

        <ListItemText
          primary={menu.label}
          primaryTypographyProps={{ noWrap: true }}
        />
        {/* ログアウトは右端アイコンを出さない */}
      </ListItemButton>
    );
  }

  /**
   * ===== 固定メニュー =====
   */
  if (menu.type === "static") {
    // 外部リンクの場合は true
    const isExternal = menu.external;

    // projects 用
    const isProjectsMenu = menu.path === "/projects";

    const isContractRequestEdit =
      location.pathname === "/user_contract_request/edit";

    // jobs 用
    const isJobsMenu = menu.path === "/jobs";

    const isJobRequestEdit = location.pathname === "/user_job_request/edit";

    /**
     * 基本情報は new/edit 両方を active 扱い
     */
    const isSkillSheetBasic =
      menu.path === "/skill_sheet_basics/edit" &&
      (location.pathname === "/skill_sheet_basics/edit" ||
        location.pathname === "/skill_sheet_basics/new");

    /**
     * active 判定
     */
    const isActive =
      !isExternal &&
      (location.pathname === menu.path ||
        location.pathname.startsWith(`${menu.path}/`) ||
        isSkillSheetBasic ||
        (isProjectsMenu && isContractRequestEdit) ||
        (isJobsMenu && isJobRequestEdit));

    return (
      <ListItemButton
        component={isExternal ? "a" : "button"}
        href={isExternal ? menu.path : undefined}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        selected={isActive}
        onClick={(e) => {
          // 無効化中
          if (isDisabled) {
            e.preventDefault();
            return;
          }

          // 外部リンクは別タブ
          if (isExternal) return;

          // 内部リンクはrequestNavigate 経由で遷移
          e.preventDefault();

          let targetPath = menu.path;

          /**
           * 基本情報：初回のみ new に遷移、それ以外は edit
           */
          if (
            menu.path === "/skill_sheet_basics/edit" &&
            isSkillSheetBasicCompleted === false
          ) {
            targetPath = "/skill_sheet_basics/new";
          }

          /**
           * redirectRule（希望条件未作成など）
           */
          if (menu.redirectRule) {
            const { key, redirectTo } = menu.redirectRule;

            if (key === "jobRequestCreated" && userJobRequestExists === false) {
              targetPath = redirectTo;
            }
          }

          requestNavigate?.(targetPath);
        }}
        sx={{
          width: "100%",
          position: "relative",
          color: TEXT_COLOR,
          borderBottom: `1px solid ${DIVIDER_COLOR}`,
          backgroundColor: "transparent",

          // 無効化時の見た目
          ...(isDisabled && {
            opacity: 0.45,
            pointerEvents: "none",
          }),

          "&:hover": {
            backgroundColor: HOVER_BG,
          },

          "&.Mui-selected": {
            backgroundColor: ACTIVE_BG,
          },

          "&.Mui-selected:hover": {
            backgroundColor: ACTIVE_BG,
          },

          "&.Mui-selected:hover::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundColor: ACTIVE_HOVER_OVERLAY,
          },
        }}
      >
        {/* 左アイコン */}
        <ListItemIcon sx={{ minWidth: 36, color: TEXT_COLOR }}>
          <menu.icon />
        </ListItemIcon>

        {/* テキスト + 未読バッジ */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ListItemText
            primary={menu.label}
            primaryTypographyProps={{ noWrap: true }}
          />
          {isNoticeLabel && hasUnreadNotice && <UnreadBadge show />}
        </Box>

        {/* 右アイコン */}
        <Box sx={{ ml: "auto", color: ICON_COLOR }}>
          {isExternal ? (
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          )}
        </Box>
      </ListItemButton>
    );
  }

  /**
   * ===== 動的メニュー（参画プロジェクト）=====
   */

  // 子のどれかが active か
  const hasActiveChild =
    menu.children?.some(
      (child) =>
        location.pathname === child.path ||
        location.pathname.startsWith(`${child.path}/`),
    ) ?? false;

  // active な子があれば自動で open
  useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  return (
    <Box>
      {/* 親メニュー */}
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          color: TEXT_COLOR,
          backgroundColor: hasActiveChild ? ACTIVE_BG : "transparent",
          borderBottom: `1px solid ${DIVIDER_COLOR}`,

          ...(isDisabled && {
            opacity: 0.45,
            pointerEvents: "none",
          }),

          "&:hover": {
            backgroundColor: hasActiveChild ? ACTIVE_BG : HOVER_BG,
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, color: TEXT_COLOR }}>
          <menu.icon />
        </ListItemIcon>

        <ListItemText
          primary={menu.label}
          primaryTypographyProps={{ noWrap: true }}
        />

        <Box sx={{ ml: "auto", color: ICON_COLOR }}>
          {open ? (
            <ExpandLess sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMore sx={{ fontSize: 16 }} />
          )}
        </Box>
      </ListItemButton>

      {/* 子メニュー */}
      {open &&
        menu.children?.map((child) => {
          const isChildActive =
            location.pathname === child.path ||
            location.pathname.startsWith(`${child.path}/`);

          return (
            <ListItemButton
              key={`work-history-${child.id}`}
              component="button"
              selected={isChildActive}
              onClick={() => requestNavigate?.(child.path)}
              sx={{
                width: "100%",
                position: "relative",
                color: TEXT_COLOR,
                backgroundColor: "transparent",
                display: "flex",
                justifyContent: "flex-end",
                py: 0.75,
                borderBottom: "1px solid rgba(255,255,255,0.08)",

                "&:hover": { backgroundColor: HOVER_BG },

                "&.Mui-selected": { backgroundColor: ACTIVE_BG },
                "&.Mui-selected:hover": { backgroundColor: ACTIVE_BG },

                "&.Mui-selected:hover::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  backgroundColor: ACTIVE_HOVER_OVERLAY,
                },
              }}
            >
              <ListItemText
                primary={child.period}
                secondary={child.label}
                sx={{ textAlign: "right" }}
                primaryTypographyProps={{
                  fontSize: FONT_SIZE.XS,
                  color: TEXT_COLOR,
                }}
                secondaryTypographyProps={{
                  fontSize: FONT_SIZE.XS,
                  color: TEXT_COLOR,
                  fontWeight: 400,
                  noWrap: true,
                }}
              />
            </ListItemButton>
          );
        })}

      {/* 子がない場合 */}
      {open && (!menu.children || menu.children.length === 0) && (
        <Box
          sx={{
            pl: 6,
            py: 1,
            opacity: 0.6,
            fontSize: FONT_SIZE.XS,
            borderBottom: `1px solid ${DIVIDER_COLOR}`,
          }}
        >
          まだプロジェクトがありません
        </Box>
      )}
    </Box>
  );
};

export default SidebarItem;
